import React from 'react';

/**
 * MANDATORY ARCHITECTURAL REQUIREMENT:
 * All search engine scripts (JSON-LD) must ideally be injected via the 'generateMetadata' API 
 * in the page.tsx file to ensure they are rendered in the <head>.
 * This component is a fallback for legacy support and should be used with caution.
 */

interface JSONLDProps {
    type: 'SoftwareApplication' | 'WebPage' | 'Article' | 'FAQPage' | 'Organization' | 'WebSite' | 'BreadcrumbList' | 'CollectionPage' | 'Graph' | 'HowTo' | 'ProfilePage' | 'Person';
    data: any;
    id?: string;
}

export default function JSONLD({ type, data, id }: JSONLDProps) {
    // Special handling for Graph type to avoid @type: "Graph" injection
    const jsonLd = type === 'Graph'
        ? { ...data }
        : {
            '@context': 'https://schema.org',
            '@type': type,
            ...data,
        };

    return (
        <script
            id={id}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            suppressHydrationWarning
        />
    );
}

// Helper for SoftwareApplication (Tools)
export function ToolJSONLD({ name, description, url, image = 'https://corptoolset.com/logo.svg', applicationCategory = 'MultimediaApplication' }: { name: string, description: string, url: string, image?: string, applicationCategory?: string }) {
    if (!name) return null;
    return (
        <JSONLD
            type="SoftwareApplication"
            data={{
                name,
                description,
                url,
                image,
                applicationCategory,
                operatingSystem: 'Windows, macOS, Linux, Android, iOS',
                author: {
                    "@type": "Organization",
                    "name": "CorpToolset Engineering Lab",
                    "url": "https://corptoolset.com/about"
                },
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                    availability: 'https://schema.org/InStock',
                    shippingDetails: {
                        "@type": "OfferShippingDetails",
                        "shippingRate": {
                            "@type": "MonetaryAmount",
                            "value": "0",
                            "currency": "USD"
                        },
                        "deliveryTime": {
                            "@type": "ShippingDeliveryTime",
                            "handlingTime": {
                                "@type": "QuantitativeValue",
                                "minValue": 0,
                                "maxValue": 0,
                                "unitCode": "DAY"
                            },
                            "transitTime": {
                                "@type": "QuantitativeValue",
                                "minValue": 0,
                                "maxValue": 0,
                                "unitCode": "DAY"
                            }
                        }
                    },
                    hasMerchantReturnPolicy: {
                        "@type": "MerchantReturnPolicy",
                        "applicableCountry": "IN",
                        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnPeriod",
                        "merchantReturnDays": 0,
                        "returnMethod": "https://schema.org/ReturnByMail",
                        "returnFees": "https://schema.org/FreeReturn"
                    }
                }
            }}
        />
    );
}

// Helper for FAQPage
export function FAQJSONLD({ questions }: { questions: { question: string, answer: string }[] }) {
    return (
        <JSONLD
            type="FAQPage"
            data={{
                mainEntity: questions.map(q => ({
                    '@type': 'Question',
                    name: q.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: q.answer,
                    }
                }))
            }}
        />
    );
}
